import { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'

export interface Reaction {
  id: string
  userId: string
  username: string
  type: 'like' | 'love' | 'laugh' | 'wow' | 'sad' | 'angry'
  timestamp: Date
}

interface ReactionSystemProps {
  targetId: string
  targetType: 'thread' | 'post' | 'comment'
  reactions: Reaction[]
  onReact: (type: Reaction['type']) => void
  onRemoveReaction: () => void
  showLabels?: boolean
  size?: 'small' | 'medium' | 'large'
}

const reactionEmojis = {
  like: '👍',
  love: '❤️',
  laugh: '😂',
  wow: '😮',
  sad: '😢',
  angry: '😠'
}

const reactionLabels = {
  like: 'Like',
  love: 'Love',
  laugh: 'Laugh',
  wow: 'Wow',
  sad: 'Sad',
  angry: 'Angry'
}

export default function ReactionSystem({
  targetId,
  targetType,
  reactions,
  onReact,
  onRemoveReaction,
  showLabels = false,
  size = 'medium'
}: ReactionSystemProps) {
  const { user, isLoggedIn } = useAuth()
  const [showReactionPicker, setShowReactionPicker] = useState(false)
  const [showReactionList, setShowReactionList] = useState(false)

  const userReaction = reactions.find(r => r.userId === user?.id)
  const reactionCounts = reactions.reduce((acc, reaction) => {
    acc[reaction.type] = (acc[reaction.type] || 0) + 1
    return acc
  }, {} as Record<Reaction['type'], number>)

  const totalReactions = reactions.length
  const topReactions = Object.entries(reactionCounts)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 3)

  const handleReactionClick = (type: Reaction['type']) => {
    if (!isLoggedIn) {
      // TODO: Show login prompt
      return
    }

    if (userReaction) {
      if (userReaction.type === type) {
        onRemoveReaction()
      } else {
        onReact(type)
      }
    } else {
      onReact(type)
    }
    setShowReactionPicker(false)
  }

  const handleQuickReact = () => {
    if (!isLoggedIn) return
    
    if (userReaction) {
      onRemoveReaction()
    } else {
      onReact('like')
    }
  }

  return (
    <div className={`reaction-system ${size}`}>
      <div className="reaction-controls">
        {/* Quick Like Button */}
        <button
          onClick={handleQuickReact}
          className={`quick-react-btn ${userReaction ? 'active' : ''} ${!isLoggedIn ? 'disabled' : ''}`}
          disabled={!isLoggedIn}
          title={userReaction ? `Remove ${reactionLabels[userReaction.type]}` : 'Like'}
        >
          <span className="reaction-icon">
            {userReaction ? reactionEmojis[userReaction.type] : reactionEmojis.like}
          </span>
          {showLabels && (
            <span className="reaction-label">
              {userReaction ? reactionLabels[userReaction.type] : 'Like'}
            </span>
          )}
        </button>

        {/* Reaction Picker Toggle */}
        {isLoggedIn && (
          <div className="reaction-picker-container">
            <button
              onClick={() => setShowReactionPicker(!showReactionPicker)}
              className="reaction-picker-toggle"
              title="Choose reaction"
            >
              <span className="more-reactions">+</span>
            </button>

            {showReactionPicker && (
              <div className="reaction-picker">
                {Object.entries(reactionEmojis).map(([type, emoji]) => (
                  <button
                    key={type}
                    onClick={() => handleReactionClick(type as Reaction['type'])}
                    className={`reaction-option ${userReaction?.type === type ? 'active' : ''}`}
                    title={reactionLabels[type as Reaction['type']]}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Share Button */}
        <button
          onClick={() => {
            // Simple share functionality
            const url = `${window.location.origin}/community/${targetType}/${targetId}`
            if (navigator.share) {
              navigator.share({
                title: `Check out this ${targetType}`,
                url: url
              })
            } else {
              navigator.clipboard.writeText(url)
              // TODO: Show toast notification
              console.log('Link copied to clipboard!')
            }
          }}
          className="share-btn"
          title="Share"
        >
          <span className="share-icon">🔗</span>
          {showLabels && <span className="share-label">Share</span>}
        </button>
      </div>

      {/* Reaction Summary */}
      {totalReactions > 0 && (
        <div className="reaction-summary">
          <button
            onClick={() => setShowReactionList(!showReactionList)}
            className="reaction-count-btn"
            title="View who reacted"
          >
            <div className="reaction-icons">
              {topReactions.map(([type, count]) => (
                <span key={type} className="reaction-emoji">
                  {reactionEmojis[type as Reaction['type']]}
                </span>
              ))}
            </div>
            <span className="reaction-count">{totalReactions}</span>
          </button>

          {showReactionList && (
            <div className="reaction-list-modal">
              <div className="reaction-list-content">
                <div className="reaction-list-header">
                  <h4>Reactions</h4>
                  <button
                    onClick={() => setShowReactionList(false)}
                    className="close-btn"
                  >
                    ×
                  </button>
                </div>
                
                <div className="reaction-tabs">
                  <button className="reaction-tab active">
                    All ({totalReactions})
                  </button>
                  {Object.entries(reactionCounts).map(([type, count]) => (
                    <button key={type} className="reaction-tab">
                      {reactionEmojis[type as Reaction['type']]} {count}
                    </button>
                  ))}
                </div>

                <div className="reaction-users">
                  {reactions
                    .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
                    .map(reaction => (
                      <div key={reaction.id} className="reaction-user">
                        <span className="reaction-emoji">
                          {reactionEmojis[reaction.type]}
                        </span>
                        <span className="username">{reaction.username}</span>
                        <span className="reaction-time">
                          {reaction.timestamp.toLocaleDateString()}
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}